import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Plus, Search, Calendar, Video, Phone, Users, CheckCircle, XCircle, Clock, Eye, Trash2, ChevronDown } from 'lucide-react';
import './RecruitmentDashboard.css';

const statusColors = {
    Scheduled: { bg: '#dbeafe', color: '#1d4ed8' },
    Completed: { bg: '#dcfce7', color: '#166534' },
    Cancelled: { bg: '#fee2e2', color: '#991b1b' },
    Pending: { bg: '#fef9c3', color: '#854d0e' },
};

const typeIcons = { Video: <Video size={13} />, Phone: <Phone size={13} />, 'In-Person': <Users size={13} /> };

const ScheduleInterview = () => {
    const [interviews, setInterviews] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [showModal, setShowModal] = useState(false);
    const [viewInterview, setViewInterview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({ candidateId: '', jobId: '', date: '', type: 'Video', status: 'Scheduled', notes: '' });

    useEffect(() => { fetchInterviews(); fetchCandidates(); }, []);

    const fetchInterviews = async () => {
        try { const res = await api.get('/recruitment/interviews'); setInterviews(res.data); }
        catch (err) { console.error("Failed to load interviews", err); }
        finally { setLoading(false); }
    };

    const fetchCandidates = async () => {
        try { const res = await api.get('/recruitment/candidates'); setCandidates(res.data); }
        catch (err) { console.error("Failed to load candidates", err); }
    };

    const handleCandidateChange = (e) => {
        const c = candidates.find(c => c._id === e.target.value);
        setFormData({ ...formData, candidateId: e.target.value, jobId: c?.jobId?._id || c?.jobId || '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            await api.post('/recruitment/interviews', { ...formData, interviewerId: user._id });
            setShowModal(false);
            setFormData({ candidateId: '', jobId: '', date: '', type: 'Video', status: 'Scheduled', notes: '' });
            fetchInterviews();
        } catch (err) { console.error(err); alert('Failed to schedule interview'); }
    };

    const handleStatusUpdate = async (id, status) => {
        // Optimistic update — change UI immediately, revert on failure
        const prev = interviews.find(i => i._id === id);
        setInterviews(list => list.map(i => i._id === id ? { ...i, status } : i));
        if (viewInterview?._id === id) setViewInterview(v => ({ ...v, status }));
        try {
            await api.put(`/recruitment/interviews/${id}`, { status });
        } catch (err) {
            console.error('Status update failed:', err);
            // Revert on failure
            if (prev) {
                setInterviews(list => list.map(i => i._id === id ? { ...i, status: prev.status } : i));
                if (viewInterview?._id === id) setViewInterview(v => ({ ...v, status: prev.status }));
            }
            alert(`Failed to update status: ${err?.response?.data?.message || err.message}`);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this interview?')) return;
        try { await api.delete(`/recruitment/interviews/${id}`); fetchInterviews(); }
        catch (err) { alert('Failed to delete'); }
    };

    const filtered = interviews.filter(i => {
        const name = `${i.candidateId?.firstName || ''} ${i.candidateId?.lastName || ''}`.toLowerCase();
        const matchSearch = name.includes(searchTerm.toLowerCase()) || (i.jobId?.title || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus = statusFilter === 'All' || i.status === statusFilter;
        return matchSearch && matchStatus;
    });

    const stats = {
        total: interviews.length,
        scheduled: interviews.filter(i => i.status === 'Scheduled').length,
        completed: interviews.filter(i => i.status === 'Completed').length,
        cancelled: interviews.filter(i => i.status === 'Cancelled').length,
    };

    if (loading) return (
        <div className="recruitment-container">
            {/* Header */}
            <div className="recruitment-header">
                <div>
                    <div className="skeleton skeleton-title" style={{ width: 200, marginBottom: 8 }} />
                    <div className="skeleton skeleton-text" style={{ width: 220 }} />
                </div>
                <div className="skeleton skeleton-btn" style={{ width: 175 }} />
            </div>

            {/* 4 Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                {[1,2,3,4].map(i => (
                    <div key={i} style={{ background: 'white', borderRadius: 10, padding: '16px 20px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div className="skeleton skeleton-text" style={{ width: 72, marginBottom: 8 }} />
                            <div className="skeleton" style={{ width: 52, height: 30, borderRadius: 6 }} />
                        </div>
                        <div className="skeleton" style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0 }} />
                    </div>
                ))}
            </div>

            {/* Filters bar */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', background: 'white', padding: '14px 18px', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <div className="skeleton" style={{ flex: 1, height: 36, borderRadius: 8 }} />
                <div className="skeleton" style={{ width: 120, height: 36, borderRadius: 8 }} />
            </div>

            {/* Table */}
            <div className="dark-card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Header row — 7 cols */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '11px 14px', borderBottom: '1.5px solid #e8edf2', background: '#f8fafc', gap: 0 }}>
                    {[{ w: 165 }, { w: 120 }, { w: 100 }, { w: 80 }, { w: 110 }, { w: 90 }, { w: 55 }].map((col, i) => (
                        <div key={i} className="skeleton" style={{ height: 11, borderRadius: 4, width: col.w, marginRight: i < 6 ? 14 : 0, flexShrink: 0 }} />
                    ))}
                </div>
                {/* Data rows */}
                {[1,2,3,4,5,6].map(i => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #f1f5f9', gap: 0 }}>
                        {/* Candidate: avatar + name + email */}
                        <div style={{ width: 165, flexShrink: 0, marginRight: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
                            <div className="skeleton" style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0 }} />
                            <div>
                                <div className="skeleton skeleton-text" style={{ width: 88, marginBottom: 5 }} />
                                <div className="skeleton skeleton-text" style={{ width: 105 }} />
                            </div>
                        </div>
                        {/* Job Position */}
                        <div className="skeleton skeleton-text" style={{ width: 110, flexShrink: 0, marginRight: 14 }} />
                        {/* Date & Time stacked */}
                        <div style={{ width: 100, flexShrink: 0, marginRight: 14 }}>
                            <div className="skeleton skeleton-text" style={{ width: '80%', marginBottom: 5 }} />
                            <div className="skeleton skeleton-text" style={{ width: '60%' }} />
                        </div>
                        {/* Type: icon + label */}
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', width: 80, flexShrink: 0, marginRight: 14 }}>
                            <div className="skeleton" style={{ width: 14, height: 14, borderRadius: 3 }} />
                            <div className="skeleton skeleton-text" style={{ width: 50 }} />
                        </div>
                        {/* Interviewer */}
                        <div className="skeleton skeleton-text" style={{ width: 95, flexShrink: 0, marginRight: 14 }} />
                        {/* Status badge (select) */}
                        <div className="skeleton" style={{ width: 90, height: 26, borderRadius: 20, flexShrink: 0, marginRight: 14 }} />
                        {/* Actions: eye + trash */}
                        <div style={{ display: 'flex', gap: 6 }}>
                            <div className="skeleton" style={{ width: 30, height: 30, borderRadius: 6 }} />
                            <div className="skeleton" style={{ width: 30, height: 30, borderRadius: 6 }} />
                        </div>
                    </div>
                ))}
                {/* Footer */}
                <div style={{ padding: '14px 20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    <div className="skeleton skeleton-text" style={{ width: 170 }} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="recruitment-container">
            {/* Header */}
            <div className="recruitment-header">
                <div>
                    <h2>Interview Schedule</h2>
                    <div className="breadcrumb"><span className="bc-parent">Recruit</span><span className="bc-sep"> • </span><span className="bc-current">Interview Schedule</span></div>
                </div>
                <button onClick={() => setShowModal(true)} className="rc-btn rc-btn-primary">
                    <Plus size={16} /> Schedule Interview
                </button>
            </div>

            {/* Stats */}
            <div className="rc-stat-grid">
                {[
                    { label: 'Total', val: stats.total, color: '#6366f1', bg: '#eef2ff', icon: <Calendar size={20} /> },
                    { label: 'Scheduled', val: stats.scheduled, color: '#3b82f6', bg: '#eff6ff', icon: <Clock size={20} /> },
                    { label: 'Completed', val: stats.completed, color: '#10b981', bg: '#ecfdf5', icon: <CheckCircle size={20} /> },
                    { label: 'Cancelled', val: stats.cancelled, color: '#ef4444', bg: '#fff1f2', icon: <XCircle size={20} /> },
                ].map((s, i) => (
                    <div key={i} className="rc-stat-card">
                        <div>
                            <div className="rc-stat-label">{s.label}</div>
                            <div className="rc-stat-value" style={{ color: s.color }}>{s.val}</div>
                        </div>
                        <div className="rc-stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="rc-filters">
                <div className="rc-search-wrap">
                    <Search size={15} />
                    <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by candidate or job..." />
                </div>
                <select className="rc-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="All">All Status</option>
                    {['Scheduled', 'Completed', 'Cancelled', 'Pending'].map(s => <option key={s}>{s}</option>)}
                </select>
            </div>

            {/* Table */}
            <div className="dark-card" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="dark-table">
                    <thead>
                        <tr>
                            <th>Candidate</th>
                            <th>Job Position</th>
                            <th>Date & Time</th>
                            <th>Type</th>
                            <th>Interviewer</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length > 0 ? filtered.map(interview => {
                            const sc = statusColors[interview.status] || { bg: '#f1f5f9', color: '#475569' };
                            const dt = interview.date ? new Date(interview.date) : null;
                            return (
                                <tr key={interview._id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                                                {interview.candidateId?.firstName?.[0]}{interview.candidateId?.lastName?.[0]}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>{interview.candidateId?.firstName} {interview.candidateId?.lastName}</div>
                                                <div style={{ fontSize: 11, color: '#94a3b8' }}>{interview.candidateId?.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ color: '#475569', fontSize: 13 }}>{interview.jobId?.title || '—'}</td>
                                    <td>
                                        {dt ? (
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>{dt.toLocaleDateString()}</div>
                                                <div style={{ fontSize: 11, color: '#94a3b8' }}>{dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            </div>
                                        ) : '—'}
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569' }}>
                                            {typeIcons[interview.type] || null} {interview.type}
                                        </div>
                                    </td>
                                    <td style={{ fontSize: 13, color: '#475569' }}>
                                        {interview.interviewerId?.firstName ? `${interview.interviewerId.firstName} ${interview.interviewerId.lastName}` : '—'}
                                    </td>
                                    <td>
                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                            <select value={interview.status} onChange={e => handleStatusUpdate(interview._id, e.target.value)}
                                                style={{ ...sc, padding: '4px 28px 4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}>
                                                {['Scheduled', 'Pending', 'Completed', 'Cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                            <ChevronDown size={12} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: sc.color }} />
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <button onClick={() => setViewInterview(interview)} title="View" className="rc-icon-btn"><Eye size={14} /></button>
                                            <button onClick={() => handleDelete(interview._id)} title="Delete" className="rc-icon-btn danger"><Trash2 size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>No interviews found. Click "Schedule Interview" to create one.</td></tr>
                        )}
                    </tbody>
                </table>
                <div className="rc-table-footer">
                    Showing {filtered.length} of {interviews.length} interviews
                </div>
            </div>

            {/* Schedule Interview Modal */}
            {showModal && (
                <div className="rc-modal-overlay">
                    <div className="rc-modal">
                        <div className="rc-modal-header">
                            <h3 className="rc-modal-title">Schedule Interview</h3>
                            <button className="rc-modal-close" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Candidate *</label>
                                <select required value={formData.candidateId} onChange={handleCandidateChange}>
                                    <option value="">Select Candidate</option>
                                    {candidates.map(c => <option key={c._id} value={c._id}>{c.firstName} {c.lastName} — {c.jobId?.title || 'No job'}</option>)}
                                </select>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label>Date & Time *</label>
                                    <input type="datetime-local" required value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Interview Type</label>
                                    <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                                        <option value="Video">Video Call</option>
                                        <option value="Phone">Phone Call</option>
                                        <option value="In-Person">In-Person</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Status</label>
                                <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                                    <option>Scheduled</option><option>Pending</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Notes</label>
                                <textarea rows={3} value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Any notes or instructions..." />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                                <button type="button" onClick={() => setShowModal(false)} className="rc-btn rc-btn-secondary">Cancel</button>
                                <button type="submit" className="rc-btn rc-btn-primary">Schedule</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Interview Modal */}
            {viewInterview && (
                <div className="rc-modal-overlay">
                    <div className="rc-modal">
                        <div className="rc-modal-header">
                            <h3 className="rc-modal-title">Interview Details</h3>
                            <button className="rc-modal-close" onClick={() => setViewInterview(null)}>×</button>
                        </div>
                        <div style={{ padding: 24 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                                {[
                                    { l: 'Candidate', v: `${viewInterview.candidateId?.firstName || ''} ${viewInterview.candidateId?.lastName || ''}` },
                                    { l: 'Job', v: viewInterview.jobId?.title || '—' },
                                    { l: 'Date', v: viewInterview.date ? new Date(viewInterview.date).toLocaleString() : '—' },
                                    { l: 'Type', v: viewInterview.type },
                                    { l: 'Interviewer', v: viewInterview.interviewerId?.firstName ? `${viewInterview.interviewerId.firstName} ${viewInterview.interviewerId.lastName}` : '—' },
                                    { l: 'Status', v: viewInterview.status, isStatus: true },
                                ].map((f, i) => (
                                    <div key={i} style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{f.l}</div>
                                        <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>
                                            {f.isStatus ? <span style={{ ...(statusColors[f.v] || {}), padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{f.v}</span> : f.v}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {viewInterview.notes && (
                                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                                    <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
                                    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{viewInterview.notes}</div>
                                </div>
                            )}
                            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, marginBottom: 8 }}>UPDATE STATUS</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {['Scheduled', 'Pending', 'Completed', 'Cancelled'].map(s => {
                                        const sc = statusColors[s] || {};
                                        const isActive = viewInterview.status === s;
                                        return (
                                            <button key={s} onClick={() => handleStatusUpdate(viewInterview._id, s)}
                                                style={{ padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: isActive ? 'none' : '1px solid #e2e8f0', background: isActive ? sc.bg : 'white', color: isActive ? sc.color : '#64748b' }}>
                                                {s}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScheduleInterview;
